/**
 * Error Boundary Component
 * Catches and handles React errors gracefully with user-friendly messages
 */

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log the error to an error reporting service here
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    
    if (this.state.retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
    } else {
      // Too many retries, redirect to a safe page
      window.location.href = '/login';
    }
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback, showDetails = false, maxRetries = 3 } = this.props;
      const { error, errorInfo, retryCount } = this.state;

      // Use custom fallback component if provided
      if (Fallback) {
        return (
          <Fallback 
            error={error}
            errorInfo={errorInfo}
            onRetry={this.handleRetry}
            retryCount={retryCount}
            canRetry={retryCount < maxRetries}
          />
        );
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="dashboard-card p-8 text-center max-w-md w-full">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" 
                />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>
            
            <p className="text-gray-600 mb-4">
              We encountered an unexpected error. This might be a temporary issue.
            </p>

            {/* Show technical details if enabled */}
            {showDetails && error && (
              <details className="mb-4 text-sm text-gray-500 text-left">
                <summary className="cursor-pointer hover:text-gray-700 text-center">
                  Technical Details
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded font-mono text-xs">
                  <div className="font-semibold mb-1">Error:</div>
                  <div className="mb-2">{error.toString()}</div>
                  {errorInfo?.componentStack && (
                    <>
                      <div className="font-semibold mb-1">Component Stack:</div>
                      <div>{errorInfo.componentStack}</div>
                    </>
                  )}
                </div>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              {retryCount < maxRetries && (
                <button 
                  onClick={this.handleRetry}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
                </button>
              )}
              
              <button 
                onClick={() => window.location.href = '/'}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Go Home
              </button>
              
              <button 
                onClick={() => window.location.reload()}
                className="bg-primary-color text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Auth-specific Error Fallback Component
 */
export const AuthErrorFallback = ({ 
  error, 
  onRetry, 
  retryCount = 0, 
  canRetry = true 
}) => {
  const getErrorMessage = () => {
    const message = error?.message || '';
    
    if (message.includes('network') || message.includes('fetch')) {
      return {
        title: 'Connection Problem',
        message: 'Unable to connect to authentication services. Please check your internet connection.',
        suggestion: 'Try again in a moment or check your network settings.'
      };
    }
    
    if (message.includes('oauth') || message.includes('token')) {
      return {
        title: 'Authentication Error',
        message: 'There was a problem with the sign-in process.',
        suggestion: 'Please try signing in again.'
      };
    }
    
    if (message.includes('unauthorized') || message.includes('access denied')) {
      return {
        title: 'Access Denied',
        message: 'You do not have permission to access this application.',
        suggestion: 'Contact your administrator to request access.'
      };
    }
    
    return {
      title: 'Authentication Failed',
      message: 'An unexpected error occurred during authentication.',
      suggestion: 'Please try again or contact support if the problem persists.'
    };
  };

  const errorContent = getErrorMessage();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="dashboard-card p-8 text-center max-w-md w-full">
        <div className="text-red-500 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {errorContent.title}
        </h2>
        
        <p className="text-gray-600 mb-2">
          {errorContent.message}
        </p>
        
        <p className="text-sm text-gray-500 mb-6">
          {errorContent.suggestion}
        </p>

        <div className="flex gap-3 justify-center">
          {canRetry && (
            <button 
              onClick={onRetry}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Try Again {retryCount > 0 && `(${retryCount}/3)`}
            </button>
          )}
          
          <button 
            onClick={() => window.location.href = '/login'}
            className="bg-primary-color text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;